import { useState, useEffect, useRef } from "react";
import { ChevronDown, ChevronUp, Search, Trophy, Loader2, Dices, Book, Trash2, List, X } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { getAllPoets } from "@/services/poetService";
import { getAllPoems, getPoemsByPoet } from "@/services/poemService";
import { createDeathmatch, addRoundToDeathmatch } from "@/services/deathmatchService";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import Cookies from "js-cookie";
import { Database } from "@/types/supabase";

// Define types based on Supabase schema
// Revert to original Poem type definition used in this component
type Poem = {
  id: string | null;
  title: string | null;
  content: string | null;
  poet_id: string | null;
  poet_name: string | null;
  subject: string | null;
  is_generated?: boolean | null;
  created_at?: string | null;
  view_count?: number | null;
  likes?: number | null;
  is_archived?: boolean | null;
};

// Revert to original Poet type definition used in this component
type Poet = {
  id: string;
  name: string;
  bio: string | null;
  style_description: string | null;
  poetry_foundation_url: string | null;
  style_points: any; // Keep as any for simplicity based on original code
  era?: string | null;
  created_at?: string | null; // Allow null to match potential DB value
};

// Fallback data in case database is empty
const fallbackPoetData = {
  "Elizabeth Bishop": ["The Fish", "One Art", "Sestina", "The Armadillo"],
  "Robert Frost": ["The Road Not Taken", "Stopping by Woods on a Snowy Evening", "Birches", "Fire and Ice"],
  "Mary Oliver": ["Wild Geese", "The Summer Day", "The Journey", "When Death Comes"],
  "Walt Whitman": ["O Captain! My Captain!", "Song of Myself", "I Hear America Singing", "Crossing Brooklyn Ferry"],
  "Emily Dickinson": ["Hope is the thing with feathers", "Because I could not stop for Death", "I'm Nobody! Who are you?", "A Bird came down the Walk"],
  "Langston Hughes": ["Harlem", "The Negro Speaks of Rivers", "I, Too", "Dream Variations"],
  "Sylvia Plath": ["Lady Lazarus", "Daddy", "Mirror", "The Colossus"],
  "T.S. Eliot": ["The Love Song of J. Alfred Prufrock", "The Waste Land", "The Hollow Men", "Ash Wednesday"],
};

// Define a type for bracket position
type BracketPosition = {
  poet: string;
  poemIndex: number;
  poemId: string; // Keep as string
  cleared?: boolean; // Add optional cleared flag
};

const Deathmatch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  // State for data
  const [poets, setPoets] = useState<Poet[]>([]);
  const [poems, setPoems] = useState<Poem[]>([]);
  const [poetPoemMap, setPoetPoemMap] = useState<Record<string, Poem[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // State for subject selection - Initialize using location state if available
  const initialSubject = location.state?.subject || "Moose"; // Get initial subject or default
  const [availableSubjects, setAvailableSubjects] = useState<string[]>([]); // Re-add state definition
  const [selectedSubject, setSelectedSubject] = useState<string>(initialSubject);
  const [showSubjectList, setShowSubjectList] = useState(false);

  // Refs for subject list UI
  const subjectListRef = useRef<HTMLDivElement>(null);
  const subjectListButtonRef = useRef<HTMLButtonElement>(null);

  // State for animation
  const [animationDirection, setAnimationDirection] = useState<'up' | 'down' | null>(null);

  // Add back state for clear dialog
  const [showClearDialog, setShowClearDialog] = useState(false);

  // State for subject change confirmation dialog
  const [showSubjectChangeDialog, setShowSubjectChangeDialog] = useState(false);
  const [subjectToChangeTo, setSubjectToChangeTo] = useState<string | null>(null);

  // Get saved bracket positions from cookie if available
  const getSavedBracketPositions = (): BracketPosition[] => {
    try {
      const savedPositions = Cookies.get('deathmatch-positions');
      if (savedPositions) {
        const parsed = JSON.parse(savedPositions);
        // Check if these are explicitly cleared positions
        if (parsed.length > 0 && parsed[0].cleared) {
          console.log("Found explicitly cleared positions");
          return parsed;
        }
        return parsed;
      }
    } catch (e) {
      console.error("Error parsing saved deathmatch positions:", e);
    }
    
    // Return empty positions if no saved data or error parsing
    return Array(8).fill(0).map(() => ({
      poet: "",
      poemIndex: 0,
      poemId: ""
    }));
  };
  
  // State for bracket - initialize with saved positions if available
  const [bracketPositions, setBracketPositions] = useState(getSavedBracketPositions());

  // Update the useEffect that watches bracketPositions to save to cookie
  useEffect(() => {
    // Save current bracket positions to cookie whenever they change
    Cookies.set('deathmatch-positions', JSON.stringify(bracketPositions), { expires: 30 }); // expires in 30 days
  }, [bracketPositions]);

  const [searchTexts, setSearchTexts] = useState(bracketPositions.map(bp => bp.poet));
  const [activeSearchIndex, setActiveSearchIndex] = useState<number | null>(null);

  // Keep searchTexts in sync with bracketPositions when poets are loaded
  useEffect(() => {
    setSearchTexts(bracketPositions.map(bp => bp.poet));
  }, [bracketPositions]);

  // Define pairings for all rounds of the tournament
  const pairings: Array<[number, number]> = [
    // Quarterfinals (first round)
    [0, 1],
    [2, 3],
    [4, 5],
    [6, 7],
    // Semifinals (winners from first round)
    // These positions will be determined by the winners array in the contest
    [0, 2], // Winner of pair 0-1 vs Winner of pair 2-3
    [4, 6], // Winner of pair 4-5 vs Winner of pair 6-7
    // Final (winners from semifinals)
    [0, 4]  // Winner of first semifinal vs Winner of second semifinal
  ];
  
  // Re-add useEffect to Extract unique subjects from poems
  useEffect(() => {
    if (poems.length > 0) {
      const subjects = Array.from(new Set(poems.map(poem => poem.subject).filter(Boolean))) as string[];
      // Add "All" option to the beginning
      setAvailableSubjects(["All", ...subjects.sort()]); 
    }
  }, [poems]);

  // Fetch poets and poems on component mount or when selectedSubject changes
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch all poets and poems (use selectedSubject for filtering poems if needed, 
        // though currently getAllPoems/getAllPoets don't seem to filter by subject directly here)
        // The filtering happens later when randomizing/selecting.
        console.log(`fetchData triggered for subject: ${selectedSubject}`);
        const poetsData = await getAllPoets();
        const poemsData = await getAllPoems({ includeArchived: false }); // Keep excluding archived
        
        if (poetsData.length === 0 || poemsData.length === 0) {
          setError("Not enough data in the database to create a deathmatch.");
          setIsLoading(false);
          
          toast({
            title: "Data Missing",
            description: "Not enough poets or poems in the database. Using fallback data.",
            variant: "destructive",
          });
          
          // Use fallback data
          setPoets(Object.keys(fallbackPoetData).map(name => ({
            id: name,
            name,
            bio: null,
            style_description: null,
            poetry_foundation_url: null,
            style_points: null,
            created_at: new Date().toISOString()
          })));
          
          // Create fallback poem map
          const fallbackMap: Record<string, Poem[]> = {};
          Object.entries(fallbackPoetData).forEach(([poet, titles]) => {
            fallbackMap[poet] = titles.map((title, index) => ({
              id: `${poet}-${index}`,
              title,
              content: `Sample poem content for ${title}`,
              poet_id: poet,
              poet_name: poet,
              subject: "Sample",
              is_generated: false,
              created_at: new Date().toISOString(),
              view_count: 0,
              likes: 0,
              is_archived: false
            }));
          });
          
          setPoetPoemMap(fallbackMap);
          
          // Check if we have saved positions before initializing random poets
          const savedPositions = getSavedBracketPositions();
          
          // Check if positions were explicitly cleared - don't initialize if so
          const wasExplicitlyCleared = savedPositions.length > 0 && savedPositions[0]?.cleared; // Safe access
          
          // Only initialize with random poets if no explicit clearing happened
          if (!wasExplicitlyCleared) {
            const hasValidSavedPositions = savedPositions.some((pos: BracketPosition) => 
              pos.poet && pos.poet.trim() !== ''
            );
            
            // Only initialize with random poets if we don't have valid saved positions
            if (!hasValidSavedPositions) {
              // Initialize brackets with only poets who have Moose poems in fallback data
              const moosePoets = Object.keys(fallbackPoetData).filter(poet => {
                const poems = (fallbackPoetData as Record<string, string[]>)[poet] || [];
                return poems.some((title: string) => title.toLowerCase().includes("moose"));
              });
              
              if (moosePoets.length >= 8) {
                // Take 8 random poets with Moose poems
                const shuffled = [...moosePoets].sort(() => 0.5 - Math.random());
                const selectedPoets = shuffled.slice(0, 8);
                
                setBracketPositions(selectedPoets.map(poet => ({
                  poet,
                  poemIndex: 0,
                  poemId: ""
                })));
                
                setSearchTexts(selectedPoets);
              }
            }
          }
          
          setIsLoading(false);
          return;
        }
        
        setPoets(poetsData as Poet[]);
        setPoems(poemsData as Poem[]);
        
        // Create a map of poet names to their poems
        const poemMap: Record<string, Poem[]> = {};
        poetsData.forEach(poet => {
          const poetPoems = poemsData.filter(poem => poem.poet_id === poet.id);
          if (poetPoems.length > 0) {
            poemMap[poet.name] = poetPoems as Poem[];
          }
        });
        
        setPoetPoemMap(poemMap);
        
        // --- Initialization Logic ---
        const savedPositions = getSavedBracketPositions();
        const wasExplicitlyCleared = savedPositions.length > 0 && savedPositions[0]?.cleared;
        const hasValidSavedPositions = savedPositions.some((pos: BracketPosition) => pos.poet && pos.poet.trim() !== '');
        const incomingSubject = location.state?.subject;

        if (incomingSubject && incomingSubject === selectedSubject) {
             console.log(`Subject "${selectedSubject}" received from navigation. Clearing bracket and randomizing.`);
             // If subject came from state (abandoned flow), clear bracket and randomize for this subject
             setBracketPositions(Array(8).fill(0).map(() => ({ poet: "", poemIndex: 0, poemId: "" })));
             setSearchTexts(Array(8).fill(""));
             randomizeBracket(selectedSubject, poetsData, poemsData, poemMap);
             // Clear the location state after using it to prevent re-triggering on refresh
             navigate(location.pathname, { replace: true, state: {} }); 
        } else if (!wasExplicitlyCleared && hasValidSavedPositions) {
             console.log(`Restoring valid saved positions from cookie for subject: ${selectedSubject}`);
             // If not abandoned and valid positions exist in cookie, restore them
             setSearchTexts(savedPositions.map(bp => bp.poet));
             // Ensure the restored positions are actually valid for the current subject? (Might be complex)
             // For now, assume cookie takes precedence unless abandoning.
        } else if (!wasExplicitlyCleared) {
             console.log(`No valid saved positions or incoming subject. Randomizing bracket for: ${selectedSubject}`);
             // If not abandoned, not cleared, and no valid cookie state, randomize for the current subject
             randomizeBracket(selectedSubject, poetsData, poemsData, poemMap);
        } else {
            console.log("Bracket was explicitly cleared or abandoning without subject match, leaving empty.");
            // If explicitly cleared, leave it empty
             setBracketPositions(Array(8).fill(0).map(() => ({ poet: "", poemIndex: 0, poemId: "" })));
             setSearchTexts(Array(8).fill(""));
        }
        // --- End Initialization Logic ---
        
        setIsLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data. Please try again later.");
        setIsLoading(false);
        
        toast({
          title: "Error",
          description: "Failed to load data. Please try again later.",
          variant: "destructive",
        });
      }
    };
    
    fetchData();
  }, [toast, selectedSubject]);

  // Function to randomize the bracket based on a subject
  const randomizeBracket = (
    subject: string, 
    allPoets: Poet[], 
    allPoems: Poem[], 
    poemMapping: Record<string, Poem[]>
  ) => {
    console.log(`Randomizing bracket for subject: ${subject}`);
    
    // Filter poets based on the selected subject
    const poetsForSubject = allPoets.filter(poet => {
      const poetPoems = poemMapping[poet.name] || [];
      if (subject === "All") {
        // Include poets with any non-archived poems
        return poetPoems.some(poem => poem.is_archived !== true);
      } else {
        // Include poets with non-archived poems matching the specific subject
        return poetPoems.some(poem => 
          poem.subject === subject && 
          poem.is_archived !== true
        );
      }
    });

    if (poetsForSubject.length < 8) {
      toast({
        title: "Limited Selection",
        description: `Only ${poetsForSubject.length} poets have poems for "${subject}". We need at least 8 for a full bracket. Try selecting 'All'.`,
        variant: "destructive",
      });
       // Clear existing bracket if not enough poets found
      setBracketPositions(Array(8).fill(0).map(() => ({ poet: "", poemIndex: 0, poemId: "" })));
      setSearchTexts(Array(8).fill(""));
      return; // Stop randomization
    }

    // Shuffle and take 8 poets
    const shuffled = [...poetsForSubject].sort(() => 0.5 - Math.random());
    const selectedPoets = shuffled.slice(0, 8);

    // Update bracket positions - TRY CASTING HERE TO FIX LINTER
    setBracketPositions(prevPositions =>
      selectedPoets.map((poet, index) => {
        const poetPoems = poemMapping[poet.name] || [];
        // Filter poems based on the selected subject
        const subjectPoems = subject === "All" 
          ? poetPoems.filter(p => p.is_archived !== true) 
          : poetPoems.filter(p => p.subject === subject && p.is_archived !== true);
        
        const poemId = (subjectPoems.length > 0 ? subjectPoems[0]?.id : null) ?? "";
        const poemIndex = subjectPoems.length > 0 
           ? poetPoems.findIndex(p => p.id === poemId) // Find original index
           : 0; 

        return {
          poet: poet.name,
          poemIndex: poemIndex >= 0 ? poemIndex : 0, // Use original index if found
          poemId: poemId
        } as BracketPosition; // Cast the final object
      })
    );

    // Update search texts
    setSearchTexts(selectedPoets.map(poet => poet.name));
  };

  const getPoemTitle = (position: number): string => {
    const { poet, poemIndex } = bracketPositions[position];
    
    // If poet is empty, return an empty string
    if (!poet || poet.trim() === '') {
      return "";
    }
    
    // Try to get from database
    const poetPoems = poetPoemMap[poet];
    if (poetPoems && poetPoems.length > 0 && poemIndex < poetPoems.length) {
      return poetPoems[poemIndex]?.title ?? "Untitled Poem";
    }
    
    // Fallback to hardcoded data - fix TypeScript errors
    const fallbackData = fallbackPoetData as Record<string, string[]>;
    // Handle potential undefined poet or poem index
    return fallbackData[poet]?.[poemIndex] ?? "Untitled Poem";
  };

  const getPoemId = (position: number) => {
    const { poet, poemIndex } = bracketPositions[position];
    
    // Try to get from database
    const poetPoems = poetPoemMap[poet];
    if (poetPoems && poetPoems.length > 0) {
      return poetPoems[poemIndex % poetPoems.length]?.id || "";
    }
    
    return "";
  };

  const cycleToNextPoem = (position: number) => {
     setBracketPositions(prev => {
      const updated = [...prev];
      const { poet, poemIndex, poemId } = updated[position];

      const poetPoems = poetPoemMap[poet] || [];
      const availablePoems = selectedSubject === "All"
         ? poetPoems.filter(p => p.is_archived !== true)
         : poetPoems.filter(p => p.subject === selectedSubject && p.is_archived !== true);

      if (availablePoems.length <= 1) return prev; // No other poems to cycle to

      // Find the current poem's index within the *filtered* list
      const currentFilteredIndex = availablePoems.findIndex(p => p.id === poemId);
      
      // Determine the next index in the *filtered* list
      const nextFilteredIndex = (currentFilteredIndex + 1) % availablePoems.length;
      const nextPoem = availablePoems[nextFilteredIndex];

      // Find the original index of the next poem in the *full* poet list
      const originalNextIndex = poetPoems.findIndex(p => p.id === nextPoem.id);

      updated[position] = {
        ...updated[position],
        // Store the original index for consistency with getPoemTitle
        poemIndex: originalNextIndex >= 0 ? originalNextIndex : 0, 
        poemId: nextPoem.id ?? ""
      };
      
      return updated;
    });
  };

  const getRandomUnusedPoet = () => {
    const usedPoets = bracketPositions.map(bp => bp.poet);
    
    // Try to get from database first, filtering to poets with non-archived Moose poems
    const availablePoets = poets
      .filter(poet => {
        // Don't include already used poets
        if (usedPoets.includes(poet.name)) return false;
        
        // Check if this poet has any non-archived Moose poems
        const poetPoems = poetPoemMap[poet.name] || [];
        return poetPoems.some(poem => 
          // Handle potential null title safely
          poem.title?.toLowerCase().includes("moose") && poem.is_archived !== true
        );
      })
      .map(poet => poet.name);
    
    if (availablePoets.length > 0) {
      const randomIndex = Math.floor(Math.random() * availablePoets.length);
      return availablePoets[randomIndex];
    }
    
    // Fallback to hardcoded data
    const fallbackPoets = Object.keys(fallbackPoetData).filter(poet => !usedPoets.includes(poet));
    
    if (fallbackPoets.length === 0) return Object.keys(fallbackPoetData)[0];
    
    const randomIndex = Math.floor(Math.random() * fallbackPoets.length);
    return fallbackPoets[randomIndex];
  };

  const updatePoet = (position: number, newPoet: string) => {
    console.log(`Updating position ${position} with poet: ${newPoet}`);
    
    setBracketPositions(prev => {
      const updated = [...prev];
      
      // --- Handle potential duplicates ---
      const existingPosition = prev.findIndex(bp => bp.poet === newPoet);
      if (existingPosition !== -1 && existingPosition !== position) {
        console.log(`Found duplicate at position ${existingPosition}, replacing with random poet`);
        
        // Get poets suitable for the current subject
         const poetsForSubject = poets.filter(poet => {
          const poetPoems = poetPoemMap[poet.name] || [];
          const subjectFilter = selectedSubject === "All"
             ? poetPoems.some(p => p.is_archived !== true)
             : poetPoems.some(p => p.subject === selectedSubject && p.is_archived !== true);
          return subjectFilter && !prev.map(bp => bp.poet).includes(poet.name); // Exclude already used poets
        });

        const randomPoetName = poetsForSubject.length > 0 
            ? poetsForSubject[Math.floor(Math.random() * poetsForSubject.length)].name 
            : getRandomUnusedPoet(); // Fallback if no suitable poets left

        const randomPoetPoems = poetPoemMap[randomPoetName] || [];
        const filteredRandomPoems = selectedSubject === "All" 
            ? randomPoetPoems.filter(p => p.is_archived !== true) 
            : randomPoetPoems.filter(p => p.subject === selectedSubject && p.is_archived !== true);
        
        const randomPoemId = filteredRandomPoems[0]?.id ?? "";
        const randomPoemIndex = randomPoetPoems.findIndex(p => p.id === randomPoemId);

        updated[existingPosition] = { 
          poet: randomPoetName, 
          poemIndex: randomPoemIndex >= 0 ? randomPoemIndex : 0,
          poemId: randomPoemId
        };

        // Update the search text for the existing position
        setSearchTexts(prevSearch => {
          const updatedSearchTexts = [...prevSearch];
          updatedSearchTexts[existingPosition] = randomPoetName;
          return updatedSearchTexts;
        });
      }
      // --- End duplicate handling ---

      // Get poems for the newly selected poet based on the current subject
      const poetPoems = poetPoemMap[newPoet] || [];
      const filteredPoems = selectedSubject === "All"
         ? poetPoems.filter(p => p.is_archived !== true)
         : poetPoems.filter(p => p.subject === selectedSubject && p.is_archived !== true);

      const poemId = filteredPoems[0]?.id ?? "";
      const poemIndex = poetPoems.findIndex(p => p.id === poemId);
      
      updated[position] = { 
        poet: newPoet, 
        poemIndex: poemIndex >= 0 ? poemIndex : 0,
        poemId: poemId
      };
      
      return updated;
    });
    
    // Update search text
    setSearchTexts(prev => {
      const updated = [...prev];
      updated[position] = newPoet;
      return updated;
    });
    
    setActiveSearchIndex(null);
  };

  const getFilteredPoets = (searchText: string): string[] => {
    // Filter poets based on whether they have poems for the selected subject
    const poetsForSubject = poets.filter(poet => {
      const poetPoems = poetPoemMap[poet.name] || [];
      if (selectedSubject === "All") {
        return poetPoems.some(poem => poem.is_archived !== true);
      } else {
        return poetPoems.some(poem => 
          poem.subject === selectedSubject && 
          poem.is_archived !== true
        );
      }
    }).map(poet => poet.name);

    if (!searchText.trim()) {
      return poetsForSubject;
    }
    
    const lowerSearchText = searchText.toLowerCase();
    return poetsForSubject.filter(poetName => 
      poetName.toLowerCase().includes(lowerSearchText)
    );
  };

  const handleSearchChange = (position: number, value: string) => {
    setSearchTexts(prev => {
      const updated = [...prev];
      updated[position] = value;
      return updated;
    });

    // If the search field is cleared, we should reset the poet at this position
    if (!value.trim()) {
      setBracketPositions(prev => {
        const updated = [...prev];
        // Reset this bracket position to have an empty poet
        updated[position] = {
          ...updated[position],
          poet: "", // Empty poet indicates not selected
          poemIndex: 0,
          poemId: ""
        };
        return updated;
      });
    }
  };

  const isPoetUsed = (poet: string) => {
    return bracketPositions.some(bp => bp.poet === poet);
  };

  // Helper function to get poet ID by name, returning null if not found
  const getPoetIdByName = (poetName: string): string | null => {
    const poet = poets.find(p => p.name === poetName);
    return poet?.id || null; // Return null if not found
  };

  // Function to handle subject selection from dropdown/buttons
  const selectSubject = (newSubject: string) => {
    // Hide the list immediately
    setShowSubjectList(false);
    
    if (newSubject === selectedSubject) {
      return; // No change
    }

    console.log("User selected subject:", newSubject);

    // Check if any current selections would become invalid
    let becomesInvalid = false;
    const hasSelections = bracketPositions.some(pos => pos.poet && pos.poet.trim() !== '');

    if (hasSelections) {
      for (const position of bracketPositions) {
        if (position.poet && position.poet.trim() !== '' && position.poemId) {
          const poetPoems = poetPoemMap[position.poet] || [];
          const poemsForNewSubject = newSubject === "All"
            ? poetPoems.filter(p => p.is_archived !== true)
            : poetPoems.filter(p => p.subject === newSubject && p.is_archived !== true);
          
          // Check if the current poemId exists in the list of poems for the NEW subject
          if (!poemsForNewSubject.some(p => p.id === position.poemId)) {
            becomesInvalid = true;
            break; // Found an invalid selection, no need to check further
          }
        }
      }
    }

    if (becomesInvalid) {
      console.log("Subject change would invalidate selections. Showing dialog.");
      setSubjectToChangeTo(newSubject);
      setShowSubjectChangeDialog(true);
    } else {
      console.log("Subject change is valid or bracket empty. Applying directly.");
      // Directly update the subject - the useEffect will handle fetching/randomizing if needed
      setSelectedSubject(newSubject);
    }
  };

  // Subject navigation functions (adapted from Book.tsx)
  const nextSubject = () => {
    if (availableSubjects.length <= 1) return;
    const currentIndex = availableSubjects.indexOf(selectedSubject);
    const nextIndex = (currentIndex + 1) % availableSubjects.length;
    setAnimationDirection('up');
    selectSubject(availableSubjects[nextIndex]);
  };

  const prevSubject = () => {
    if (availableSubjects.length <= 1) return;
    const currentIndex = availableSubjects.length - 1;
    const prevIndex = (currentIndex - 1 + availableSubjects.length) % availableSubjects.length;
    setAnimationDirection('down');
    selectSubject(availableSubjects[prevIndex]);
  };

  // Handle click outside for subject list
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showSubjectList && 
          subjectListRef.current && 
          !subjectListRef.current.contains(event.target as Node) &&
          subjectListButtonRef.current && 
          !subjectListButtonRef.current.contains(event.target as Node)) {
        setShowSubjectList(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSubjectList]); // Only depends on subject list state now

   // Update the handleStartDeathmatch function
  const handleStartDeathmatch = () => {
    try {
      // Check if all positions have poets selected
      const allPositionsHavePoets = bracketPositions.every(position => position.poet && position.poet.trim() !== '');
      if (!allPositionsHavePoets) {
         toast({ title: "Missing Selections", description: "Please select poets for all bracket positions.", variant: "destructive" });
         return;
      }

      // Ensure each selected poet has at least one valid poem for the current subject
      const updatedPositions = [...bracketPositions];
      let missingPoems = false;
      let poemsUpdated = false;

      updatedPositions.forEach((position, index) => {
        const poetPoems = poetPoemMap[position.poet] || [];
        const subjectPoems = selectedSubject === "All" 
           ? poetPoems.filter(p => p.is_archived !== true)
           : poetPoems.filter(p => p.subject === selectedSubject && p.is_archived !== true);

        // If no poems found for the subject OR current poemId is invalid for the subject
        if (subjectPoems.length === 0 || !subjectPoems.some(p => p.id === position.poemId)) {
           if (subjectPoems.length > 0) {
               // If poems exist for subject, but current poemId is wrong, select the first valid one
               const firstValidPoem = subjectPoems[0];
               const originalIndex = poetPoems.findIndex(p => p.id === firstValidPoem.id);
               updatedPositions[index] = {
                   ...position,
                   poemId: firstValidPoem.id ?? "",
                   poemIndex: originalIndex >= 0 ? originalIndex : 0
               };
               poemsUpdated = true;
               console.log(`Updated poem for ${position.poet} at position ${index} to ${firstValidPoem.title}`);
           } else {
               // No poems available for this poet/subject combo
               missingPoems = true;
               console.error(`No valid poems found for ${position.poet} with subject "${selectedSubject}" at position ${index}`);
           }
        }
      });

      if (missingPoems) {
         toast({ title: "Missing Poems", description: `Some selected poets don't have poems for the subject "${selectedSubject}". Please adjust selections or change the subject.`, variant: "destructive", duration: 5000 });
         return;
      }
      
      // If we had to update poems, update the state and maybe inform the user
      if (poemsUpdated) {
         setBracketPositions(updatedPositions);
         // Optionally add a toast here if needed
      }
      
      // Proceed if all checks pass
      const finalPositions = poemsUpdated ? updatedPositions : bracketPositions;

      const positionsWithIds = finalPositions.map(pos => ({
        ...pos,
        poet_id: getPoetIdByName(pos.poet) // Poet ID lookup
      }));

      const missingPoetId = positionsWithIds.find(pos => pos.poet_id === null);
      if (missingPoetId) {
        console.error("Failed to find Poet ID for:", missingPoetId.poet);
        toast({
          title: "Data Error",
          description: `Could not find the ID for poet "${missingPoetId.poet}". Please try refreshing or selecting a different poet.`, 
          variant: "destructive",
        });
        return; // Stop execution
      }

      // Clear relevant localStorage for the new contest
      localStorage.removeItem('deathmatch_current_pairing_index');
      localStorage.removeItem('deathmatch_winners');
      localStorage.removeItem('deathmatch_view_summary');
      localStorage.removeItem('deathmatch_timestamp');
      localStorage.removeItem('deathmatch_round_completed');
      localStorage.removeItem('deathmatch_pairings'); 
      localStorage.removeItem('deathmatch_setup'); // Also clear previous setup data
      
      // Add logging around removal
      console.log("[Deathmatch.tsx] Attempting to remove deathmatch_id from localStorage...");
      const idBefore = localStorage.getItem('deathmatch_id');
      localStorage.removeItem('deathmatch_id');
      const idAfter = localStorage.getItem('deathmatch_id');
      console.log(`[Deathmatch.tsx] deathmatch_id before remove: ${idBefore}, after remove: ${idAfter}`);

      // Prepare data for navigation state AND localStorage
      const setupData = { 
        bracketPositions: positionsWithIds, // Use positions with poet_id
        pairings: pairings,
        subject: selectedSubject
      }; 

      // Persist setup data to localStorage before navigating
      try {
        localStorage.setItem('deathmatch_setup', JSON.stringify(setupData));
        console.log("[Deathmatch.tsx] Saved deathmatch setup data to localStorage.");
      } catch (error) {
        console.error("[Deathmatch.tsx] Error saving deathmatch setup to localStorage:", error);
        toast({ title: "Warning", description: "Could not save contest setup for potential reload.", variant: "default" });
      }
      
      console.log("[Deathmatch.tsx] Starting deathmatch with subject:", selectedSubject);
      
      // Navigate to contest page, passing the same setupData object via state
      navigate('/deathmatch/contest', { 
        state: setupData 
      });

    } catch (error) {
      console.error("[Deathmatch.tsx] Error starting deathmatch:", error);
      toast({ title: "Error", description: "An unexpected error occurred while starting the deathmatch.", variant: "destructive" });
    }
  };

  // Check if any bracket position has an empty poet
  const hasEmptyPositions = bracketPositions.some(position => 
    !position.poet || position.poet.trim() === ''
  );

  const [showRandomDialog, setShowRandomDialog] = useState(false);

  // Function to fill empty spots with random selections
  const fillRandomSelections = () => {
    // Get all poets with non-archived Moose poems
    const availablePoets = poets
      .filter(poet => {
        const poetPoems = poetPoemMap[poet.name] || [];
        return poetPoems.some(poem => 
          // Handle potential null title safely
          poem.title?.toLowerCase().includes("moose") && poem.is_archived !== true
        );
      })
      .map(poet => poet.name);
    
    // If no poets with non-archived Moose poems, use fallback data
    const poetPool = availablePoets.length > 0 
      ? availablePoets 
      : Object.keys(fallbackPoetData);
    
    // Shuffle the poets for randomness
    const shuffledPoets = [...poetPool].sort(() => 0.5 - Math.random());
    
    // Update bracket positions
    setBracketPositions(prev => {
      const updated = [...prev];
      
      // Loop through each position
      updated.forEach((position, index) => {
        // If position is empty OR we're refilling all positions
        if (!position.poet || position.poet.trim() === '' || !hasEmptyPositions) {
          // Find a poet that isn't already used in another position
          const usedPoets = updated.map(pos => pos.poet).filter(Boolean);
          const availablePoet = shuffledPoets.find(poet => !usedPoets.includes(poet));
          
          if (availablePoet) {
            // Get initial poem for the poet
            let poemId = "";
            const poetPoems = poetPoemMap[availablePoet];
            if (poetPoems && poetPoems.length > 0) {
              poemId = poetPoems[0].id ?? "";
            }
            
            // Update the position
            updated[index] = {
              poet: availablePoet,
              poemIndex: 0,
              poemId
            };
            
            // Remove the poet from the shuffled list to avoid duplicates
            const poetIndex = shuffledPoets.indexOf(availablePoet);
            if (poetIndex > -1) {
              shuffledPoets.splice(poetIndex, 1);
            }
          }
        }
      });
      
      return updated;
    });
    
    // Update search texts
    setSearchTexts(bracketPositions.map(bp => bp.poet));
    
    // Close the dialog
    setShowRandomDialog(false);
  };

  // Renamed function to filter poems for a specific poet based on selected subject
  const filterPoemsForPoet = (poetName: string): Poem[] => {
    // Check if we have poems for this poet
    if (poetPoemMap[poetName] && poetPoemMap[poetName].length > 0) {
      const poetPoems = poetPoemMap[poetName];
      if (selectedSubject === "All") {
        return poetPoems.filter(p => p.is_archived !== true);
      } else {
        return poetPoems.filter(poem => 
          poem.subject === selectedSubject && 
          poem.is_archived !== true
        );
      }
    }
    
    // Fallback to hardcoded data - ensure return matches Poem[]
    const fallbackData = fallbackPoetData as Record<string, string[]>;
    const fallbackTitles = fallbackData[poetName] || [];
    // Fallback poems don't have subjects, so return all if "All", else empty
    const poemsToReturn = selectedSubject === "All" ? fallbackTitles : []; 
    return poemsToReturn
      .map((title: string | null, index: number): Poem => ({ // Return type is Poem
        id: `${poetName}-${index}`,
        title: title ?? "Untitled Poem",
        content: `Sample poem content for ${title ?? 'Untitled'}`, 
        poet_id: poetName,
        poet_name: poetName,
        subject: "Sample", // Fallback subject
        is_generated: false,
        created_at: new Date().toISOString(),
        view_count: 0,
        likes: 0,
        is_archived: false
      }));
  };

  // Function to select a specific poem for a position (ensure it uses original index)
  const selectSpecificPoem = (position: number, poemIndexInFilteredList: number, poemId: string) => {
     setBracketPositions(prev => {
      const updated = [...prev];
      const poetName = updated[position].poet;
      const poetPoems = poetPoemMap[poetName] || [];
      
      // Find the original index of the selected poem
      const originalIndex = poetPoems.findIndex(p => p.id === poemId);
      
      updated[position] = {
        ...updated[position],
        poemIndex: originalIndex >= 0 ? originalIndex : 0, // Use original index
        poemId: poemId
      };
      return updated;
    });
  };

  // Add back the handleClearSavedState function
  const handleClearSavedState = () => {
    const emptyPositions = Array(8).fill(0).map(() => ({
      poet: "",
      poemIndex: 0,
      poemId: "",
      cleared: true
    }));
    Cookies.set('deathmatch-positions', JSON.stringify(emptyPositions));
    setBracketPositions(emptyPositions);
    setSearchTexts(Array(8).fill("")); // Also clear search texts
    setShowClearDialog(false);
    toast({
      title: "Bracket Cleared",
      description: "All selections have been cleared.",
      variant: "default",
    });
  };

  // Handle navigation to poem book (ensure poemId is not null)
  const handleNavigateToPoem = (e: React.MouseEvent, poem: Poem) => {
    e.stopPropagation();
    e.preventDefault();
    const poemId = poem.id;
    const poetName = poem.poet_name ?? "";
    if (!poemId || !poetName) {
      console.error("Cannot navigate: Missing poemId or poetName", poem);
      return;
    }
    localStorage.setItem("currentPoemId", poemId as string);
    localStorage.setItem("currentPoetName", poetName);
    navigate(`/book?poemId=${encodeURIComponent(poemId as string)}&poet=${encodeURIComponent(poetName)}`); 
  };

  return (
    <div className="min-h-screen pt-16" data-testid="deathmatch-component">
      <div className="container mx-auto px-4 py-8">
        <h1 className="font-poetry text-4xl font-semibold mb-4 text-center flex items-center justify-center flex-wrap">
          Poem Deathmatch for {" "}
          <span className="relative inline-flex items-center group ml-2 mr-1">
            <Button
              variant="ghost" size="icon" onClick={prevSubject}
              className="absolute -top-5 left-1/2 transform -translate-x-1/2 h-6 w-6 opacity-60 hover:opacity-100 transition-opacity !bg-transparent"
              aria-label="Previous subject" disabled={availableSubjects.length <= 1}
              data-testid="prev-subject-button"
            > <ChevronUp className="h-4 w-4" /> </Button>
            
            <span className={`font-semibold text-primary ${animationDirection === 'up' ? 'animate-subject-up' : animationDirection === 'down' ? 'animate-subject-down' : ''}`} data-testid="subject-name">
              {selectedSubject}
            </span>

            <Button
              variant="ghost" size="icon" onClick={nextSubject}
              className="absolute -bottom-5 left-1/2 transform -translate-x-1/2 h-6 w-6 opacity-60 hover:opacity-100 transition-opacity !bg-transparent"
              aria-label="Next subject" disabled={availableSubjects.length <= 1}
              data-testid="next-subject-button"
            > <ChevronDown className="h-4 w-4" /> </Button>
          </span>
           <div className="relative inline-block">
              <Button
                variant="ghost" size="icon" ref={subjectListButtonRef}
                onClick={() => setShowSubjectList(!showSubjectList)}
                className="h-8 w-8 opacity-80 hover:opacity-100 transition-opacity hover:bg-gray-100"
                aria-label="Show subject list"
              > <List className="h-4 w-4" /> </Button>
              {showSubjectList && (
                <div 
                  ref={subjectListRef}
                  className="absolute z-50 mt-1 bg-white rounded-md shadow-lg border border-gray-200 p-2 max-h-80 overflow-y-auto custom-scrollbar w-64 left-1/2 transform -translate-x-1/2"
                 >
                  <div className="flex justify-between items-center mb-2 pb-2 border-b">
                    <h3 className="font-medium text-sm">Select Subject</h3>
                    <Button variant="ghost" size="icon" onClick={() => setShowSubjectList(false)} className="h-6 w-6 hover:bg-gray-100 rounded-full" aria-label="Close subject list">
                      <X className="h-4 w-4" /> </Button>
                  </div>
                  {availableSubjects.map((subject) => (
                    <button key={subject} onClick={() => selectSubject(subject)}
                      className={`block w-full text-left px-4 py-2 text-sm rounded-md ${subject === selectedSubject ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-gray-100'}`}
                    > {subject} </button>
                  ))}
                </div>
              )}
            </div>
        </h1>
        
        <div className="text-center mb-4">
          {/* Help text now appears first */}
          <div className="mx-auto max-w-3xl mb-6 p-4 bg-amber-50/80 border border-amber-200 rounded-md shadow-sm flex items-center gap-4">
            <img src="/images/lowresmoose.png" alt="Moose" className="w-28 h-auto flex-shrink-0 rounded-md"/>
            <div className="space-y-2 text-left">
              <p className="text-sm text-gray-700 leading-relaxed"> A Deathmatch is a silly format that guides you through reading eight poems. Begin by selecting poems (or click the dice). When you start, you'll see pairs of poems. Choose one poem from each pair to move on. After the final round, only one poem remains. </p>
              <p className="text-sm text-gray-700"> For each bracket, choose a poet, then choose a poem inspired by that poet's style about <strong>{selectedSubject === "All" ? "any subject" : selectedSubject}</strong>. </p>
            </div>
          </div>

          {/* Buttons group moved below help text */}
          <div className="flex items-center justify-center gap-2 mb-4">
            <Button variant="default" className="px-6" onClick={handleStartDeathmatch} disabled={hasEmptyPositions} data-testid="start-deathmatch-button"> Start Deathmatch </Button>
            <Button variant="outline" size="icon" className="transition-all hover:bg-gray-100 active:bg-gray-200" onClick={() => setShowRandomDialog(true)} title="Random fill"> <Dices className="h-5 w-5" /> </Button>
            <Button variant="outline" className="transition-all hover:bg-gray-100 active:bg-gray-200" onClick={() => setShowClearDialog(true)}> Clear All Brackets </Button>
          </div>

          {hasEmptyPositions && ( <div className="text-sm text-gray-800 mt-2"> You have to choose all eight poets before you start the Deathmatch </div> )}
        </div>
        
        <Dialog open={showRandomDialog} onOpenChange={setShowRandomDialog}> <DialogContent className="sm:max-w-md"> <DialogHeader> <DialogTitle>Random Selection</DialogTitle> <DialogDescription> {hasEmptyPositions ? "Fill all empty spots with random selections?" : "Refill all bracket cards with random selections?"} </DialogDescription> </DialogHeader> <DialogFooter className="flex gap-2 sm:justify-end"> <Button type="button" variant="outline" onClick={() => setShowRandomDialog(false)}>No</Button> <Button type="button" onClick={() => { randomizeBracket(selectedSubject, poets, poems, poetPoemMap); setShowRandomDialog(false); }}>Yes</Button> </DialogFooter> </DialogContent> </Dialog>
        <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}> <DialogContent className="sm:max-w-md"> <DialogHeader> <DialogTitle>Clear Bracket</DialogTitle> <DialogDescription> Clear all the poet and poem selections on this page? </DialogDescription> </DialogHeader> <DialogFooter className="flex gap-2 sm:justify-end"> <Button type="button" variant="outline" onClick={() => setShowClearDialog(false)}>No</Button> <Button type="button" onClick={handleClearSavedState}>Yes</Button> </DialogFooter> </DialogContent> </Dialog>
        
        {/* Subject Change Confirmation Dialog */}
        <AlertDialog open={showSubjectChangeDialog} onOpenChange={setShowSubjectChangeDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Subject Change</AlertDialogTitle>
              <AlertDialogDescription>
                Changing the subject to "{subjectToChangeTo}" will clear all current bracket selections because some poems won't match. Do you want to continue?
                <br/>
                <span className="text-sm text-muted-foreground mt-2 block">To compare poems with different subjects, choose 'All'.</span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex sm:justify-between sm:items-center">
              {/* Add the 'All' button on the left */}
              <Button 
                variant="outline"
                onClick={() => {
                  setSelectedSubject("All"); // Set subject to All
                  setShowSubjectChangeDialog(false); // Close dialog
                  setSubjectToChangeTo(null); // Reset pending subject
                  // Do NOT clear the bracket
                }}
              >
                All
              </Button>
              {/* Keep Cancel and Continue on the right */}
              <div className="flex gap-2">
                <AlertDialogCancel onClick={() => setSubjectToChangeTo(null)}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    handleClearSavedState(); // Clear the bracket and cookie
                    if (subjectToChangeTo) {
                      setSelectedSubject(subjectToChangeTo); // Apply the new subject
                    }
                    setSubjectToChangeTo(null); // Reset the pending subject
                  }}
                >
                  Continue
                </AlertDialogAction>
              </div>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <div className="w-full overflow-x-auto">
          <div className="w-full py-8 max-w-6xl mx-auto">
            <div className="flex justify-between">
              <div className="flex flex-col space-y-20 w-[280px]">
                <div className="space-y-4">
                  <BracketCard 
                    position={0} 
                    poet={bracketPositions[0].poet} 
                    poemTitle={getPoemTitle(0)} 
                    searchText={searchTexts[0]} 
                    isActive={activeSearchIndex === 0} 
                    onSearchChange={text => handleSearchChange(0, text)} 
                    onCyclePoem={() => cycleToNextPoem(0)} 
                    onPoetSelect={poet => updatePoet(0, poet)} 
                    onFocus={() => setActiveSearchIndex(0)} 
                    onBlur={() => setActiveSearchIndex(null)} 
                    filteredPoets={getFilteredPoets(searchTexts[0])} 
                    isPoetUsed={isPoetUsed} 
                    poetPoemMap={poetPoemMap} 
                    updateBracketPosition={selectSpecificPoem} 
                    filterPoemsForPoet={filterPoemsForPoet} 
                    selectedSubject={selectedSubject} 
                    onNavigateToPoem={handleNavigateToPoem}
                  />
                  <BracketCard 
                    position={1} 
                    poet={bracketPositions[1].poet} 
                    poemTitle={getPoemTitle(1)} 
                    searchText={searchTexts[1]} 
                    isActive={activeSearchIndex === 1} 
                    onSearchChange={text => handleSearchChange(1, text)} 
                    onCyclePoem={() => cycleToNextPoem(1)} 
                    onPoetSelect={poet => updatePoet(1, poet)} 
                    onFocus={() => setActiveSearchIndex(1)} 
                    onBlur={() => setActiveSearchIndex(null)} 
                    filteredPoets={getFilteredPoets(searchTexts[1])} 
                    isPoetUsed={isPoetUsed} 
                    poetPoemMap={poetPoemMap} 
                    updateBracketPosition={selectSpecificPoem} 
                    filterPoemsForPoet={filterPoemsForPoet} 
                    selectedSubject={selectedSubject} 
                    onNavigateToPoem={handleNavigateToPoem}
                  />
                </div>
                 <div className="space-y-4">
                   <BracketCard 
                    position={2} 
                    poet={bracketPositions[2].poet} 
                    poemTitle={getPoemTitle(2)} 
                    searchText={searchTexts[2]} 
                    isActive={activeSearchIndex === 2} 
                    onSearchChange={text => handleSearchChange(2, text)} 
                    onCyclePoem={() => cycleToNextPoem(2)} 
                    onPoetSelect={poet => updatePoet(2, poet)} 
                    onFocus={() => setActiveSearchIndex(2)} 
                    onBlur={() => setActiveSearchIndex(null)} 
                    filteredPoets={getFilteredPoets(searchTexts[2])} 
                    isPoetUsed={isPoetUsed} 
                    poetPoemMap={poetPoemMap} 
                    updateBracketPosition={selectSpecificPoem} 
                    filterPoemsForPoet={filterPoemsForPoet} 
                    selectedSubject={selectedSubject} 
                    onNavigateToPoem={handleNavigateToPoem}
                   />
                   <BracketCard 
                    position={3} 
                    poet={bracketPositions[3].poet} 
                    poemTitle={getPoemTitle(3)} 
                    searchText={searchTexts[3]} 
                    isActive={activeSearchIndex === 3} 
                    onSearchChange={text => handleSearchChange(3, text)} 
                    onCyclePoem={() => cycleToNextPoem(3)} 
                    onPoetSelect={poet => updatePoet(3, poet)} 
                    onFocus={() => setActiveSearchIndex(3)} 
                    onBlur={() => setActiveSearchIndex(null)} 
                    filteredPoets={getFilteredPoets(searchTexts[3])} 
                    isPoetUsed={isPoetUsed} 
                    poetPoemMap={poetPoemMap} 
                    updateBracketPosition={selectSpecificPoem} 
                    filterPoemsForPoet={filterPoemsForPoet} 
                    selectedSubject={selectedSubject} 
                    onNavigateToPoem={handleNavigateToPoem}
                   />
                 </div>
              </div>
              
              <div className="flex flex-col justify-between py-14 mx-4"> <div className="flex items-center justify-center h-24"> <SemiFinalistCard position="left-top" /> </div> <div className="flex items-center justify-center h-24"> <SemiFinalistCard position="left-bottom" /> </div> </div>
               <div className="flex flex-col justify-center items-center"> <div className="mb-6"> <FinalistCard /> </div> <div className="rounded-full p-8 bg-amber-100 border-2 border-amber-300"> <Trophy className="h-16 w-16 text-amber-600" /> </div> <div className="mt-6"> <FinalistCard /> </div> </div>
               <div className="flex flex-col justify-between py-14 mx-4"> <div className="flex items-center justify-center h-24"> <SemiFinalistCard position="right-top" /> </div> <div className="flex items-center justify-center h-24"> <SemiFinalistCard position="right-bottom" /> </div> </div>
              
              <div className="flex flex-col space-y-20 w-[280px]">
                 <div className="space-y-4">
                   <BracketCard 
                    position={4} 
                    poet={bracketPositions[4].poet} 
                    poemTitle={getPoemTitle(4)} 
                    searchText={searchTexts[4]} 
                    isActive={activeSearchIndex === 4} 
                    onSearchChange={text => handleSearchChange(4, text)} 
                    onCyclePoem={() => cycleToNextPoem(4)} 
                    onPoetSelect={poet => updatePoet(4, poet)} 
                    onFocus={() => setActiveSearchIndex(4)} 
                    onBlur={() => setActiveSearchIndex(null)} 
                    filteredPoets={getFilteredPoets(searchTexts[4])} 
                    isPoetUsed={isPoetUsed} 
                    poetPoemMap={poetPoemMap} 
                    updateBracketPosition={selectSpecificPoem} 
                    filterPoemsForPoet={filterPoemsForPoet} 
                    selectedSubject={selectedSubject} 
                    onNavigateToPoem={handleNavigateToPoem}
                   />
                   <BracketCard 
                    position={5} 
                    poet={bracketPositions[5].poet} 
                    poemTitle={getPoemTitle(5)} 
                    searchText={searchTexts[5]} 
                    isActive={activeSearchIndex === 5} 
                    onSearchChange={text => handleSearchChange(5, text)} 
                    onCyclePoem={() => cycleToNextPoem(5)} 
                    onPoetSelect={poet => updatePoet(5, poet)} 
                    onFocus={() => setActiveSearchIndex(5)} 
                    onBlur={() => setActiveSearchIndex(null)} 
                    filteredPoets={getFilteredPoets(searchTexts[5])} 
                    isPoetUsed={isPoetUsed} 
                    poetPoemMap={poetPoemMap} 
                    updateBracketPosition={selectSpecificPoem} 
                    filterPoemsForPoet={filterPoemsForPoet} 
                    selectedSubject={selectedSubject} 
                    onNavigateToPoem={handleNavigateToPoem}
                   />
                 </div>
                 <div className="space-y-4">
                   <BracketCard 
                    position={6} 
                    poet={bracketPositions[6].poet} 
                    poemTitle={getPoemTitle(6)} 
                    searchText={searchTexts[6]} 
                    isActive={activeSearchIndex === 6} 
                    onSearchChange={text => handleSearchChange(6, text)} 
                    onCyclePoem={() => cycleToNextPoem(6)} 
                    onPoetSelect={poet => updatePoet(6, poet)} 
                    onFocus={() => setActiveSearchIndex(6)} 
                    onBlur={() => setActiveSearchIndex(null)} 
                    filteredPoets={getFilteredPoets(searchTexts[6])} 
                    isPoetUsed={isPoetUsed} 
                    poetPoemMap={poetPoemMap} 
                    updateBracketPosition={selectSpecificPoem} 
                    filterPoemsForPoet={filterPoemsForPoet} 
                    selectedSubject={selectedSubject} 
                    onNavigateToPoem={handleNavigateToPoem}
                   />
                   <BracketCard 
                    position={7} 
                    poet={bracketPositions[7].poet} 
                    poemTitle={getPoemTitle(7)} 
                    searchText={searchTexts[7]} 
                    isActive={activeSearchIndex === 7} 
                    onSearchChange={text => handleSearchChange(7, text)} 
                    onCyclePoem={() => cycleToNextPoem(7)} 
                    onPoetSelect={poet => updatePoet(7, poet)} 
                    onFocus={() => setActiveSearchIndex(7)} 
                    onBlur={() => setActiveSearchIndex(null)} 
                    filteredPoets={getFilteredPoets(searchTexts[7])} 
                    isPoetUsed={isPoetUsed} 
                    poetPoemMap={poetPoemMap} 
                    updateBracketPosition={selectSpecificPoem} 
                    filterPoemsForPoet={filterPoemsForPoet} 
                    selectedSubject={selectedSubject} 
                    onNavigateToPoem={handleNavigateToPoem}
                   />
                 </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
         @keyframes subject-change-up { 0% { transform: translateY(10px); opacity: 0; } 100% { transform: translateY(0); opacity: 1; } }
         @keyframes subject-change-down { 0% { transform: translateY(-10px); opacity: 0; } 100% { transform: translateY(0); opacity: 1; } }
         .animate-subject-up { animation: subject-change-up 0.3s ease-out forwards; }
         .animate-subject-down { animation: subject-change-down 0.3s ease-out forwards; }
         .custom-scrollbar::-webkit-scrollbar { width: 6px; }
         .custom-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 6px; }
         .custom-scrollbar::-webkit-scrollbar-thumb { background: #ccc; border-radius: 6px; }
         .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #aaa; }
      `}</style>
    </div>
  );
};

const FinalistCard = () => {
  return (
    <Card className="w-40 bg-amber-50 border-2 border-amber-200">
      <CardContent className="p-3 text-center">
        <div className="text-base font-poetry font-semibold text-amber-800">
          Finalist
        </div>
      </CardContent>
    </Card>
  );
};

const SemiFinalistCard = ({ position }: { position: string }) => {
  return (
    <Card className="w-32">
      <CardContent className="p-2 text-center">
        <div className="text-sm font-poetry truncate">Semifinalist</div>
      </CardContent>
    </Card>
  );
};

interface BracketCardProps {
  position: number;
  poet: string;
  poemTitle: string;
  searchText: string;
  isActive: boolean;
  onSearchChange: (text: string) => void;
  onCyclePoem: () => void;
  onPoetSelect: (poet: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  filteredPoets: string[];
  isPoetUsed: (poet: string) => boolean;
  poetPoemMap: Record<string, Poem[]>;
  updateBracketPosition: (position: number, poemIndex: number, poemId: string) => void;
  filterPoemsForPoet: (poetName: string) => Poem[];
  selectedSubject: string;
  onNavigateToPoem: (e: React.MouseEvent, poem: Poem) => void;
}

const BracketCard = ({
  position,
  poet,
  poemTitle,
  searchText,
  isActive,
  onSearchChange,
  onCyclePoem,
  onPoetSelect,
  onFocus,
  onBlur,
  filteredPoets,
  isPoetUsed,
  poetPoemMap,
  updateBracketPosition,
  filterPoemsForPoet,
  selectedSubject,
  onNavigateToPoem
}: BracketCardProps) => {
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const poemDropdownRef = useRef<HTMLDivElement>(null);
  const poemTriggerRef = useRef<HTMLDivElement>(null);
  const [isPoemDropdownOpen, setIsPoemDropdownOpen] = useState(false);
  
  useEffect(() => {
    console.log(`Position ${position} - Poet: ${poet}, SearchText: ${searchText}, Active: ${isPoemDropdownOpen}`);
  }, [position, poet, searchText, isPoemDropdownOpen]);
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isActive && dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && inputRef.current && !inputRef.current.contains(event.target as Node)) {
          if (document.activeElement !== inputRef.current && !dropdownRef.current?.contains(document.activeElement)) {
              onBlur();
          }
      }
      if (isPoemDropdownOpen && poemDropdownRef.current && !poemDropdownRef.current.contains(event.target as Node) && poemTriggerRef.current && !poemTriggerRef.current.contains(event.target as Node)) {
        setIsPoemDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isActive, isPoemDropdownOpen, onBlur]);

  const togglePoemDropdown = () => {
    setIsPoemDropdownOpen(prev => !prev);
  };
  
  const handleSelectPoem = (poemIndexInFilteredList: number) => {
    const filteredPoems = filterPoemsForPoet(poet);
    if (filteredPoems.length > 0 && poemIndexInFilteredList < filteredPoems.length) {
      const selectedPoem = filteredPoems[poemIndexInFilteredList];
      updateBracketPosition(position, poemIndexInFilteredList, selectedPoem.id ?? ""); 
    }
    setIsPoemDropdownOpen(false);
  };
  
  const getDisplayablePoets = () => filteredPoets;
  const handleSelectPoet = (selectedPoetName: string) => { 
    onPoetSelect(selectedPoetName); 
    // No need to call onBlur here, as updatePoet will set activeSearchIndex to null
  };

  const poemsForDropdown = filterPoemsForPoet(poet);

  return (
    <Card className="overflow-visible" data-testid={position % 2 === 0 ? "left-poem" : "right-poem"}>
      <CardContent className="p-4">
        <div className="space-y-2">
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={searchText}
              onChange={(e) => onSearchChange(e.target.value)}
              onFocus={onFocus}
              onBlur={onBlur}
              placeholder="Search poets..."
              className={cn(
                "w-full p-2 pr-8 border rounded-md text-sm",
                searchText === poet && poet.trim() !== '' && "bg-gray-50 font-medium"
              )}
              data-testid={`poet-search-${position}`}
            />
            <Search className="absolute right-2 top-2 h-4 w-4 text-muted-foreground" />
            
            {isActive && filteredPoets.length > 0 && (
              <div 
                ref={dropdownRef}
                className="absolute z-50 top-full left-0 right-0 mt-1 bg-background border rounded-md shadow-lg overflow-y-auto custom-scrollbar"
                style={{ maxHeight: "200px" }}
                data-testid={`poet-dropdown-${position}`}
              >
                {getDisplayablePoets().map((poetName) => (
                  <div
                    key={poetName}
                    className={cn(
                      "p-2 text-sm cursor-pointer hover:bg-muted",
                      isPoetUsed(poetName) && poetName !== poet && "opacity-50"
                    )}
                    onMouseDown={(e) => {
                      e.preventDefault(); 
                      handleSelectPoet(poetName);
                    }}
                    data-testid={`poet-option-${position}-${poetName.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {poetName}
                    {isPoetUsed(poetName) && poetName !== poet && " (used)"}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="relative">
            <div 
              className={cn(
                "flex items-center justify-between p-1.5 mt-1 rounded border",
                !poet ? "bg-gray-100 opacity-50 cursor-not-allowed" : ""
              )}
              onClick={poet ? togglePoemDropdown : undefined}
              ref={poemTriggerRef}
            >
              <div className={cn(
                "text-sm", 
                !poet ? "text-gray-400" : (poemTitle ? "" : "text-muted-foreground")
              )}>
                {poet ? (poemTitle || "Select a poem") : "Select a poet first"}
              </div>
              {poet && <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />} 
            </div>
            
            {isPoemDropdownOpen && poet && poemsForDropdown.length > 0 && (
              <div 
                ref={poemDropdownRef}
                className="absolute z-50 top-full left-0 right-0 mt-1 bg-background border rounded-md shadow-lg overflow-y-auto custom-scrollbar"
                style={{ maxHeight: "200px" }}
                data-testid={`poem-dropdown-${position}`}
              >
                {poemsForDropdown.map((poem, index) => {
                  const currentPoem = poem;
                  const key = poem.id ? `poem-${poem.id}` : `poem-index-${index}`;
                  return (
                    <div
                      key={key}
                      className="p-2 text-sm cursor-pointer hover:bg-muted flex justify-between items-center"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSelectPoem(index);
                      }}
                      data-testid={`poem-option-${position}-${index}`}
                    >
                      <span className="mr-2 truncate">{poem.title ?? "Untitled Poem"}</span>
                      <Book 
                        className="h-4 w-4 text-gray-500 hover:text-blue-500 flex-shrink-0" 
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          onNavigateToPoem(e, currentPoem);
                        }}
                        aria-label="View in book"
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const WinnerIndicator = ({ winner }: { winner: string }) => (
  <div className="text-center p-4 bg-green-100 rounded-md mt-4" data-testid="winner-indicator">
    Winner: {winner}
  </div>
);

export default Deathmatch;
